/**
 * Entry Card Component
 *
 * Displays a single accomplishment entry with category badge,
 * satisfaction indicator, and action buttons.
 */

import { useState } from 'react';
import {
  Briefcase,
  Heart,
  Palette,
  Users,
  Home,
  BookOpen,
  Sparkles,
  Clock,
  Trash2,
  Pencil,
  MoreHorizontal,
} from 'lucide-react';
import { CATEGORY_META, useDeleteEntry, useCreateEntry, type Entry } from '@have-done/shared';
import { SatisfactionDisplay } from './SatisfactionRating';
import { ConfirmModal } from './ConfirmModal';
import { useToast } from './Toast';

const CATEGORY_ICONS = {
  work: Briefcase,
  health: Heart,
  creative: Palette,
  social: Users,
  personal: Home,
  learning: BookOpen,
} as const;

const CATEGORY_BG_COLORS = {
  work: 'bg-category-work/15',
  health: 'bg-category-health/15',
  creative: 'bg-category-creative/15',
  social: 'bg-category-social/15',
  personal: 'bg-category-personal/15',
  learning: 'bg-category-learning/15',
} as const;

const CATEGORY_BORDER_COLORS = {
  work: 'border-l-category-work',
  health: 'border-l-category-health',
  creative: 'border-l-category-creative',
  social: 'border-l-category-social',
  personal: 'border-l-category-personal',
  learning: 'border-l-category-learning',
} as const;

interface EntryCardProps {
  entry: Entry;
  showDate?: boolean;
  onEdit?: (entry: Entry) => void;
}

export function EntryCard({ entry, showDate = false, onEdit }: EntryCardProps) {
  const [showActions, setShowActions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  const deleteEntry = useDeleteEntry();
  const createEntry = useCreateEntry();
  const { showUndo, showError } = useToast();

  const Icon = CATEGORY_ICONS[entry.category];
  const meta = CATEGORY_META[entry.category];

  const handleDeleteClick = () => {
    setShowActions(false);
    setShowDeleteModal(true);
  };

  const handleEditClick = () => {
    setShowActions(false);
    onEdit?.(entry);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);

    try {
      // Store entry data for undo
      const entryData = {
        content: entry.content,
        category: entry.category,
        satisfaction: entry.satisfaction,
        timeSpentMinutes: entry.timeSpentMinutes,
        isBigWin: entry.isBigWin,
        date: entry.date,
      };

      await deleteEntry({ id: entry._id as any });

      // Optimistic UI - fade out
      setIsDeleted(true);
      setShowDeleteModal(false);

      // Show undo toast
      showUndo('Entry deleted', async () => {
        try {
          await createEntry(entryData);
        } catch {
          showError('Failed to restore entry');
        }
      });
    } catch (error) {
      console.error('Failed to delete entry:', error);
      showError('Failed to delete entry. Please try again.');
      setIsDeleting(false);
    }
  };

  const formatTime = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Don't render if deleted (for smooth transition)
  if (isDeleted) {
    return null;
  }

  return (
    <>
      <article
        className={`
          relative rounded-lg border-l-4 bg-bg-secondary p-4
          transition-all hover:shadow-warm-sm
          ${CATEGORY_BORDER_COLORS[entry.category]}
          ${isDeleting ? 'opacity-50 pointer-events-none' : ''}
        `}
      >
        {/* Header row */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Category badge */}
            <span
              className={`
                inline-flex items-center gap-1 rounded-full px-2.5 py-0.5
                text-xs font-medium
                ${CATEGORY_BG_COLORS[entry.category]}
              `}
            >
              <Icon className="h-3 w-3" aria-hidden="true" />
              {meta.label}
            </span>

            {/* Big win badge */}
            {entry.isBigWin && (
              <span className="inline-flex items-center gap-1 rounded-full bg-accent/20 px-2.5 py-0.5 text-xs font-medium text-accent-dark">
                <Sparkles className="h-3 w-3" aria-hidden="true" />
                Big win
              </span>
            )}

            {/* Date (if showing) */}
            {showDate && (
              <span className="text-xs text-text-tertiary">{formatDate(entry.date)}</span>
            )}
          </div>

          {/* Actions menu */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowActions(!showActions)}
              aria-label="Entry actions"
              aria-expanded={showActions}
              aria-haspopup="true"
              className="
                p-1 rounded-lg text-text-tertiary
                hover:bg-bg-tertiary hover:text-text-secondary
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary
              "
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>

            {showActions && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowActions(false)}
                  aria-hidden="true"
                />
                <div
                  role="menu"
                  className="absolute right-0 z-20 mt-1 w-36 rounded-lg bg-bg-primary border border-border-light shadow-warm-md overflow-hidden"
                >
                  {onEdit && (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleEditClick}
                      className="
                        flex w-full items-center gap-2 px-3 py-2 text-sm text-text-primary
                        hover:bg-bg-secondary
                        transition-colors
                      "
                    >
                      <Pencil className="h-4 w-4" />
                      Edit
                    </button>
                  )}
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleDeleteClick}
                    className="
                      flex w-full items-center gap-2 px-3 py-2 text-sm text-error
                      hover:bg-error/10
                      transition-colors
                    "
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        <p className="text-text-primary leading-relaxed">{entry.content}</p>

        {/* Footer metadata */}
        {(entry.satisfaction || entry.timeSpentMinutes) && (
          <div className="flex items-center gap-3 mt-3 text-sm text-text-secondary">
            {entry.satisfaction && <SatisfactionDisplay value={entry.satisfaction} />}
            {entry.timeSpentMinutes && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                {formatTime(entry.timeSpentMinutes)}
              </span>
            )}
          </div>
        )}
      </article>

      {/* Delete confirmation modal */}
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete entry?"
        message="This entry will be removed. You can undo this action for a few seconds after deletion."
        confirmText="Delete"
        cancelText="Keep"
        variant="danger"
        isLoading={isDeleting}
      />
    </>
  );
}

/**
 * Empty state for when there are no entries
 */
export function EntryListEmpty({ message = 'No accomplishments yet' }: { message?: string }) {
  return (
    <div className="text-center py-12">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-bg-tertiary mb-4">
        <Sparkles className="h-8 w-8 text-text-tertiary" />
      </div>
      <p className="text-text-secondary">{message}</p>
      <p className="text-sm text-text-tertiary mt-1">Log your first win above!</p>
    </div>
  );
}
